import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { listCoaEligibleLotsApi, uploadCoaApi } from 'api/coa';
import { formatFileSize } from './CoaDocumentsTable';

export const MAX_COA_FILE_BYTES = 25 * 1024 * 1024;

export const isPdfFile = (file) =>
  !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? ''));

export const lotOptionLabel = (lot) =>
  `${lot.drugName}${lot.strength ? ` ${lot.strength}` : ''} — NDC ${lot.ndc} — Lot ${lot.lotNumber}`;

const lotKey = (lot) => `${lot.ndc}_${lot.lotNumber}`;

const errorMessage = (error, fallback) =>
  error?.response?.errors?.[0]?.message || error?.message || fallback;

const UploadCoaDialog = ({ open, onClose, onUploaded }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lot, setLot] = useState(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLot(null);
    setFile(null);
    setFileError('');
    setLotsLoading(true);
    listCoaEligibleLotsApi()
      .then(setLots)
      .catch((error) => {
        enqueueSnackbar(errorMessage(error, 'Failed to load inventory lots'), {
          variant: 'error',
        });
      })
      .finally(() => setLotsLoading(false));
  }, [open, enqueueSnackbar]);

  const sortedLots = useMemo(
    () =>
      [...lots].sort(
        (a, b) => a.drugName.localeCompare(b.drugName) || a.lotNumber.localeCompare(b.lotNumber)
      ),
    [lots]
  );

  const handleFileChange = (event) => {
    const [picked] = event.target.files ?? [];
    // Reset the input so choosing the same file again still fires onChange.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!picked) return;
    if (!isPdfFile(picked)) {
      setFile(null);
      setFileError('Only PDF files are accepted');
      return;
    }
    if (picked.size > MAX_COA_FILE_BYTES) {
      setFile(null);
      setFileError(`File is too large (max ${formatFileSize(MAX_COA_FILE_BYTES)})`);
      return;
    }
    setFileError('');
    setFile(picked);
  };

  const handleSubmit = () => {
    if (!lot || !file) return;
    setSubmitting(true);
    uploadCoaApi({ ndc: lot.ndc, lotNumber: lot.lotNumber, file })
      .then((config) => {
        enqueueSnackbar(
          `${config.overwrites ? 'Replaced' : 'Uploaded'} COA ${config.fileName} for ${lot.drugName} lot ${lot.lotNumber}`,
          { variant: 'success' }
        );
        onUploaded?.(config);
        onClose();
      })
      .catch((error) => {
        enqueueSnackbar(errorMessage(error, 'Failed to upload COA'), { variant: 'error' });
      })
      .finally(() => setSubmitting(false));
  };

  const handleClose = (_, reason) => {
    /* istanbul ignore next */
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ fontWeight: 600 }}>Upload Certificate of Analysis</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Choose an NDC and lot from inventory, then attach the PDF. The file is stored as
          <Box component='code' sx={{ mx: 0.5 }}>
            NDC_LOT.pdf
          </Box>
          and replaces any certificate already on file for that lot.
        </Typography>

        <Autocomplete
          options={sortedLots}
          value={lot}
          onChange={(_, value) => setLot(value)}
          loading={lotsLoading}
          getOptionLabel={lotOptionLabel}
          isOptionEqualToValue={(a, b) => lotKey(a) === lotKey(b)}
          groupBy={(option) => option.drugName}
          noOptionsText={lotsLoading ? 'Loading inventory lots…' : 'No inventory lots found'}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label='NDC / Lot'
              placeholder='Search by drug, NDC or lot number'
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {lotsLoading ? <CircularProgress color='inherit' size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <li {...props} key={lotKey(option)}>
              <Box>
                <Typography variant='body2'>
                  NDC {option.ndc} — Lot {option.lotNumber}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {option.manufacturer}
                  {option.hasCoa ? ' · COA on file' : ''}
                </Typography>
              </Box>
            </li>
          )}
        />

        {lot?.hasCoa && (
          <Alert severity='warning'>
            A COA already exists for NDC {lot.ndc} lot {lot.lotNumber}. Uploading will replace it.
          </Alert>
        )}

        <Box>
          <Button component='label' variant='outlined' startIcon={<UploadFileIcon />}>
            {file ? 'Change PDF' : 'Choose PDF'}
            <input
              hidden
              type='file'
              accept='application/pdf,.pdf'
              data-testid='coa-file-input'
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant='body2' sx={{ mt: 1 }} color={fileError ? 'error' : 'text.primary'}>
            {fileError || (file ? `${file.name} (${formatFileSize(file.size)})` : 'No file chosen')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          onClick={handleSubmit}
          loading={submitting}
          disabled={!lot || !file}
        >
          {lot?.hasCoa ? 'Replace COA' : 'Upload COA'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

UploadCoaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploaded: PropTypes.func,
};

UploadCoaDialog.defaultProps = {
  onUploaded: undefined,
};

export default UploadCoaDialog;
